import React, { useState, useEffect, useMemo } from 'react';
import ThreadCard from '../components/threads/ThreadCard';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const Threads = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const { data, error } = await supabase
          .from('viral_threads')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (data) {
          setThreads(data.map(item => ({
            id: item.id,
            title: item.title,
            category: item.category || 'General',
            author: item.author,
            platform: item.platform,
            content: item.content,
            deepLinks: item.deep_links,
            ctaText: item.cta_text,
            ctaUrl: item.cta_url,
            date: new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })));
        }
      } catch (err) {
        console.error("Error fetching threads:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchThreads();
  }, []);

  // Dynamically extract categories handling comma-separated values
  const categories = useMemo(() => {
    const allCats = threads
      .flatMap(t => (t.category || 'General').split(',').map(c => c.trim()))
      .filter(Boolean);
    return [...new Set(allCats)].sort();
  }, [threads]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const filteredThreads = useMemo(() => {
    if (selectedCategories.length === 0) return threads;
    return threads.filter(item => {
      const itemCats = (item.category || 'General').split(',').map(c => c.trim());
      return selectedCategories.some(sc => itemCats.includes(sc));
    });
  }, [threads, selectedCategories]);

  return (
    <div className="w-full">
      {/* Header and Filter Section */}
      <div className="bg-brand-surface border-b border-brand-border pt-10 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-[800px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
            <div>
              <h1 className="text-[32px] md:text-[36px] lg:text-[40px] font-bold text-brand-text mb-4 leading-tight">Viral Threads</h1>
              <p className="text-[18px] md:text-[20px] text-brand-secondary max-w-[700px] leading-[1.55]">
                The most insightful AI discussions, breakdowns, and analyses curated from around the web.
              </p>
            </div>
            
            <button 
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`flex items-center justify-center gap-2 px-5 py-3 rounded-[8px] text-[16px] font-bold transition-colors border shrink-0 ${
                isFilterOpen || selectedCategories.length > 0
                  ? 'bg-brand-mint/10 text-brand-mint border-brand-mint/30'
                  : 'bg-brand-bg text-brand-text border-brand-border hover:bg-brand-raised'
              }`}
            >
              <SafeIcon icon="Filter" className="w-5 h-5" />
              <span>{selectedCategories.length > 0 ? `Filters (${selectedCategories.length})` : 'Filter'}</span>
              <SafeIcon icon={isFilterOpen ? "ChevronUp" : "ChevronDown"} className="w-5 h-5 ml-1 hidden sm:block" />
            </button>
          </div>

          {/* Collapsible Category Panel */}
          {isFilterOpen && (
            <div className="pt-5 pb-3 border-t border-brand-border animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-[13px] md:text-[14px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-4">Select Categories</p>
              {categories.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {categories.map(cat => (
                    <label 
                      key={cat}
                      className="flex items-center gap-2.5 cursor-pointer group px-4 py-2.5 rounded-[8px] hover:bg-brand-bg border border-transparent hover:border-brand-border transition-colors select-none"
                    >
                      <input 
                        type="checkbox"
                        className="sr-only"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => toggleCategory(cat)}
                      />
                      <div className={`w-5 h-5 rounded-[4px] border flex items-center justify-center transition-colors ${
                        selectedCategories.includes(cat) 
                          ? 'bg-brand-mint border-brand-mint' 
                          : 'border-brand-muted group-hover:border-brand-text'
                      }`}>
                        {selectedCategories.includes(cat) && <SafeIcon icon="Check" className="w-3.5 h-3.5 text-brand-bg" />}
                      </div>
                      <span className="text-[16px] md:text-[18px] text-brand-secondary group-hover:text-brand-text transition-colors">{cat}</span>
                    </label>
                  ))}
                  {selectedCategories.length > 0 && (
                    <button 
                      onClick={() => setSelectedCategories([])}
                      className="px-4 py-2.5 text-[15px] font-bold text-brand-breaking hover:opacity-80 transition-opacity ml-2"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-[16px] text-brand-secondary italic">No categories available yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feed */}
      <div className="max-w-[800px] mx-auto px-4 md:px-6 pt-12 pb-20 space-y-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <SafeIcon icon="Loader" className="w-10 h-10 text-brand-mint animate-spin" />
          </div>
        ) : filteredThreads.length > 0 ? (
          filteredThreads.map(thread => (
            <ThreadCard key={thread.id} thread={thread} />
          ))
        ) : (
          <div className="text-center">
            <SafeIcon icon="MessageSquare" className="w-16 h-16 text-brand-muted mx-auto mb-5" />
            <h3 className="text-[20px] md:text-[24px] font-bold text-brand-text mb-3">No Threads Found</h3>
            <p className="text-brand-secondary text-[18px] md:text-[20px]">
              {selectedCategories.length > 0 
                ? "Try adjusting your category filters." 
                : "We are currently gathering the top AI threads. Check back shortly."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Threads;